import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ShoppingBag, X } from "lucide-react";
import { MENU } from "../lib/menu";
import { useTray } from "../context/TrayContext";
import TrayBuilder from "../components/TrayBuilder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const AddButton = ({ dish, size, label }) => {
  const { add } = useTray();
  return (
    <button
      data-testid={`add-${size === "Full Tray" ? "full" : "half"}-${slug(dish.name)}`}
      onClick={() => add(dish, size)}
      className="group flex items-center gap-1 rounded-full border border-nosh-border bg-white px-2.5 py-1 font-poster text-[10px] font-bold uppercase tracking-wide text-nosh-ink transition-colors duration-300 hover:border-nosh-green hover:bg-nosh-green hover:text-nosh-cream"
    >
      <Plus className="h-3 w-3" />
      {label}
    </button>
  );
};

export default function Menu() {
  const { count } = useTray();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menu, setMenu] = useState(MENU);

  useEffect(() => {
    axios
      .get(`${API}/menu`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setMenu(data);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="menu-page" className="bg-nosh-cream">
      {/* Header */}
      <header className="border-b border-nosh-border bg-nosh-surface pb-8 pt-36">
        <div className="mx-auto max-w-[1300px] px-5 md:px-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="font-script text-2xl text-nosh-gold">Nosh — farmaiye!</div>
            <h1 className="font-display text-4xl font-bold leading-none text-nosh-ink sm:text-5xl">Our Menu</h1>
            <p className="mt-3 max-w-2xl font-body text-[15px] text-nosh-ink/65">
              Every dish comes in a Half or Full tray. Tap <span className="font-semibold text-nosh-green">+</span> to
              add it to your order, then pick a date and pickup/delivery on the right.
            </p>
          </motion.div>
        </div>
      </header>

      <div className="mx-auto max-w-[1300px] px-5 py-10 md:px-10 md:py-14">
        <div className="grid gap-10 lg:grid-cols-[1fr_380px] lg:gap-12">
          {/* Menu list */}
          <div className="space-y-10">
            {menu.map((section, si) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                data-testid={`menu-section-${section.id}`}
              >
                <div className="mb-3 flex items-baseline gap-3 border-b border-nosh-border pb-2">
                  <span className="font-display text-2xl font-bold text-nosh-green/25">{String(si + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-2xl font-bold leading-none text-nosh-ink sm:text-3xl">{section.title}</h2>
                  <span className="ml-auto hidden font-body text-xs italic text-nosh-ink/45 sm:block">{section.tagline}</span>
                </div>

                <ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
                  {section.items.map((item) => (
                    <li key={item.name} className="flex items-center justify-between gap-3 border-b border-nosh-border/60 py-2.5">
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                          <h3 className="truncate font-display text-base font-bold text-nosh-ink">{item.name}</h3>
                          {item.note && <span className="hidden truncate font-body text-[11px] italic text-nosh-ink/45 md:inline">({item.note})</span>}
                        </div>
                        <div className="mt-0.5 flex items-center gap-3 font-poster text-[12px] font-semibold text-nosh-ink/60">
                          <span>Half <span className="text-nosh-green">${item.half}</span></span>
                          <span>Full <span className="text-nosh-green">${item.full}</span></span>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col gap-1">
                        <AddButton dish={item} size="Half Tray" label={`Half`} />
                        <AddButton dish={item} size="Full Tray" label={`Full`} />
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.section>
            ))}
          </div>

          {/* Sticky order panel (desktop) */}
          <aside id="builder" className="hidden lg:block">
            <div className="sticky top-20">
              <TrayBuilder />
            </div>
          </aside>

          <div id="builder-mobile" className="lg:hidden">
            <TrayBuilder />
          </div>
        </div>
      </div>

      {/* Mobile floating order button */}
      <AnimatePresence>
        {count > 0 && !mobileOpen && (
          <motion.button
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-tray-fab"
            className="fixed bottom-5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full bg-nosh-green px-6 py-3.5 font-poster text-sm font-bold uppercase tracking-wide text-nosh-cream shadow-2xl lg:hidden"
          >
            <ShoppingBag className="h-5 w-5" />
            View order
            <span className="grid h-6 min-w-6 place-items-center rounded-full bg-nosh-gold px-1 text-white">{count}</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)} className="fixed inset-0 z-40 bg-nosh-ink/60 lg:hidden" />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 32, stiffness: 320 }}
              className="fixed inset-x-0 bottom-0 z-50 max-h-[88vh] overflow-y-auto rounded-t-3xl bg-nosh-cream p-4 lg:hidden"
            >
              <div className="mb-3 flex justify-end">
                <button onClick={() => setMobileOpen(false)} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-full bg-nosh-surface text-nosh-ink">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <TrayBuilder />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
